import React, { useCallback, useEffect } from 'react';
import useChurnZero from 'shared/hooks/useChurnZero';
import { useAuth } from 'shared/providers/AuthProvider';
import useClinicUser from 'shared/hooks/useClinicUser';
import { ClinicPimsIntegrationType } from 'shared/types/graphql';
import { env } from 'env';

const isProduction = env.REACT_APP_PRODUCTION === 'true';

interface IChurnZeroProps {
  children: React.ReactNode;
}

const ChurnZeroAttributes = ({ children }: IChurnZeroProps): JSX.Element => {
  const { currentClinic, hasAdminRoleAtCurrentClinic } = useClinicUser();

  const { isImpersonating } = useAuth();

  const { setAccountAttribute, setContactAttribute } = useChurnZero();

  const updateChurnZeroAndBitwerxAttributes = useCallback(() => {
    if (!isProduction || isImpersonating) {
      return;
    }
    let isBitwerxIntegratedAndActive = false;
    const stripeChargesEnabled: boolean = !!currentClinic?.submerchantStripeChargesEnabled || false;
    const stripePayoutsEnabled = !!currentClinic?.submerchantStripePayoutsEnabled || false;
    // No need to query default account since clinics can not have payouts enabled without a default account
    const stripeDefaultAccount = stripePayoutsEnabled;

    const clinicPimsIntegrations = currentClinic?.integrations || [];

    for (const integration of clinicPimsIntegrations) {
      if (integration.type === ClinicPimsIntegrationType.Bitwerx && integration.isActive) {
        isBitwerxIntegratedAndActive = true;
      }
    }
    setAccountAttribute('stripeChargesEnabled', stripeChargesEnabled.toString());
    setAccountAttribute('stripePayoutsEnabled', stripePayoutsEnabled.toString());
    setAccountAttribute('stripeDefaultAccount', stripeDefaultAccount.toString());
    setAccountAttribute('isBitwerxIntegratedAndActive', isBitwerxIntegratedAndActive.toString());
    setContactAttribute('isClinicAdmin', hasAdminRoleAtCurrentClinic.toString());
  }, [
    currentClinic?.integrations,
    currentClinic?.submerchantStripeChargesEnabled,
    currentClinic?.submerchantStripePayoutsEnabled,
    hasAdminRoleAtCurrentClinic,
    isImpersonating,
    setAccountAttribute,
    setContactAttribute,
  ]);

  useEffect(() => {
    updateChurnZeroAndBitwerxAttributes();
  }, [updateChurnZeroAndBitwerxAttributes]);

  return <>{children}</>;
};

export default ChurnZeroAttributes;
